const API_BASE = '/api';

// --- Performance-Monitoring (Level 3 + 4) ---
const PERF_LOG_MAX = 200;
window.__perfLog = window.__perfLog || [];
window.__perfEnabled = window.__perfEnabled ?? true; // Immer aktiv für Diagnose

// === LEVEL 4: Interaction Tracking ===
window.__interactionLog = window.__interactionLog || [];
const INTERACTION_LOG_MAX = 100;

export function trackInteraction(name) {
  const interaction = { name, t0: performance.now(), steps: [], done: false };

  const step = (label) => {
    interaction.steps.push({ label, ms: Math.round((performance.now() - interaction.t0) * 10) / 10 });
  };

  const end = () => {
    const totalMs = Math.round((performance.now() - interaction.t0) * 10) / 10;
    interaction.totalMs = totalMs;
    interaction.done = true;
    interaction.ts = Date.now();
    window.__interactionLog.push(interaction);
    if (window.__interactionLog.length > INTERACTION_LOG_MAX) window.__interactionLog.shift();
    if (totalMs > 300) {
      console.warn(`🐌 Langsame Interaktion: ${name} (${totalMs}ms)`, interaction.steps);
    }
  };

  // Double-rAF: misst tatsächliche Paint-Zeit
  const endAtPaint = () => {
    step('before-paint');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        step('painted');
        end();
      });
    });
  };

  return { step, end, endAtPaint };
}

export function perfMark(label) {
  if (!window.__perfEnabled) return null;
  return { label, t0: performance.now() };
}

export function perfEnd(mark, extra) {
  if (!mark) return 0;
  const ms = Math.round((performance.now() - mark.t0) * 10) / 10;
  const entry = { label: mark.label, ms, ts: Date.now(), ...extra };
  window.__perfLog.push(entry);
  if (window.__perfLog.length > PERF_LOG_MAX) window.__perfLog.shift();
  if (ms > 500) console.warn(`⏱ Langsam: ${mark.label} (${ms}ms)`, extra || '');
  return ms;
}

// SEV-010 Fix: Zentrale Fehlerbehandlung für alle API-Aufrufe
class ApiError extends Error {
  constructor(status, message, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// === LEVEL 3: JSON Parse + IPC Breakdown Messung ===
async function handleResponse(res, mark) {
  if (!res.ok) {
    let errorData;
    try {
      errorData = await res.json();
    } catch {
      errorData = { error: res.statusText };
    }
    if (mark) perfEnd(mark, { status: res.status, error: true });
    throw new ApiError(res.status, errorData.error || `HTTP ${res.status}`, errorData);
  }

  // Messen: wie lange dauert JSON.parse?
  const tParseStart = performance.now();
  const data = await res.json();
  const parseMs = Math.round((performance.now() - tParseStart) * 10) / 10;

  // IPC-Breakdown aus Response-Header lesen (nur in Electron Production vorhanden)
  let ipcBreakdown = null;
  const serverMs = res.headers.get('X-Perf-Server-Ms');
  const breakdownHeader = res.headers.get('X-Perf-Breakdown');
  if (breakdownHeader) {
    try { ipcBreakdown = JSON.parse(breakdownHeader); } catch { /* ignore */ }
  }

  if (mark) {
    perfEnd(mark, {
      status: 200,
      parseMs,
      serverMs: serverMs ? parseFloat(serverMs) : undefined,
      ipc: ipcBreakdown,
    });
  }
  return data;
}

// === Globaler Performance-Report (aufrufbar via window.__perfReport()) ===
window.__perfReport = () => {
  const log = window.__perfLog;
  const interactions = window.__interactionLog;

  // Aggregation pro Endpoint
  const byEndpoint = {};
  for (const entry of log) {
    const key = entry.label;
    if (!byEndpoint[key]) byEndpoint[key] = { count: 0, totalMs: 0, maxMs: 0, avgParseMs: 0, parseCount: 0 };
    byEndpoint[key].count++;
    byEndpoint[key].totalMs += entry.ms;
    byEndpoint[key].maxMs = Math.max(byEndpoint[key].maxMs, entry.ms);
    if (entry.parseMs) {
      byEndpoint[key].avgParseMs += entry.parseMs;
      byEndpoint[key].parseCount++;
    }
  }
  for (const key of Object.keys(byEndpoint)) {
    const e = byEndpoint[key];
    e.avgMs = Math.round(e.totalMs / e.count * 10) / 10;
    if (e.parseCount > 0) e.avgParseMs = Math.round(e.avgParseMs / e.parseCount * 10) / 10;
    else delete e.avgParseMs;
    delete e.parseCount;
  }

  const report = {
    endpoints: byEndpoint,
    recentRequests: log.slice(-20),
    interactions: interactions.slice(-20),
    ipcBreakdowns: log.filter(e => e.ipc).slice(-10).map(e => ({ label: e.label, ...e.ipc })),
  };

  console.table(Object.entries(byEndpoint).map(([k, v]) => ({ endpoint: k, ...v })));
  console.log('📊 Vollständiger Report:', report);
  return report;
};

export async function fetchBootstrap() {
  const m = perfMark('GET /bootstrap');
  const res = await fetch(`${API_BASE}/bootstrap`);
  return handleResponse(res, m);
}

export async function fetchZettel() {
  const m = perfMark('GET /zettel');
  const res = await fetch(`${API_BASE}/zettel`);
  return handleResponse(res, m);
}

export async function fetchZettelById(id) {
  const m = perfMark(`GET /zettel/${id}`);
  const res = await fetch(`${API_BASE}/zettel/${encodeURIComponent(id)}`);
  return handleResponse(res, m);
}

export async function createZettel(data) {
  const m = perfMark('POST /zettel');
  const res = await fetch(`${API_BASE}/zettel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  return handleResponse(res, m);
}

export async function updateZettel(id, data) {
  const m = perfMark(`PUT /zettel/${id}`);
  const res = await fetch(`${API_BASE}/zettel/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  return handleResponse(res, m);
}

export async function deleteZettel(id, cascade = false) {
  const m = perfMark(`DELETE /zettel/${id}`);
  const url = cascade
    ? `${API_BASE}/zettel/${encodeURIComponent(id)}?cascade=true`
    : `${API_BASE}/zettel/${encodeURIComponent(id)}`;
  const res = await fetch(url, { method: 'DELETE' });
  return handleResponse(res, m);
}

export async function moveZettel(id, targetParentId) {
  const m = perfMark(`POST /zettel/${id}/move`);
  const res = await fetch(`${API_BASE}/zettel/${encodeURIComponent(id)}/move`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetParentId })
  });
  return handleResponse(res, m);
}

export async function fetchGraph() {
  const m = perfMark('GET /graph');
  const res = await fetch(`${API_BASE}/graph`);
  return handleResponse(res, m);
}

export async function searchZettel(query) {
  const m = perfMark('GET /search');
  const res = await fetch(`${API_BASE}/search?q=${encodeURIComponent(query)}`);
  return handleResponse(res, m);
}

export async function fetchNextId(parentId = null) {
  const m = perfMark('GET /next-id');
  const url = parentId
    ? `${API_BASE}/next-id?parentId=${encodeURIComponent(parentId)}`
    : `${API_BASE}/next-id`;
  const res = await fetch(url);
  return handleResponse(res, m);
}

export async function fetchReadwise() {
  const m = perfMark('GET /readwise');
  const res = await fetch(`${API_BASE}/readwise`);
  return handleResponse(res, m);
}

// Slim: nur Metadaten + Preview + Refs (kein voller Highlight-Text)
export async function fetchReadwiseSlim() {
  const m = perfMark('GET /readwise?slim');
  const res = await fetch(`${API_BASE}/readwise?slim=true`);
  return handleResponse(res, m);
}

// Einzelne Quelle mit vollen Highlights
export async function fetchReadwiseSource(key) {
  const m = perfMark(`GET /readwise/source/${key}`);
  const res = await fetch(`${API_BASE}/readwise/source/${encodeURIComponent(key)}`);
  return handleResponse(res, m);
}

// Leichtgewichtige Map für Editor-Widgets: ref → {text, author, title}
export async function fetchHighlightDataMap() {
  const m = perfMark('GET /readwise/highlight-map');
  const res = await fetch(`${API_BASE}/readwise/highlight-map`);
  return handleResponse(res, m);
}

// PERF-007: Nur benötigte Highlight-Refs laden statt die gesamte 9MB Map
export async function fetchHighlightDataByRefs(refs) {
  const m = perfMark('POST /readwise/highlight-data');
  const res = await fetch(`${API_BASE}/readwise/highlight-data`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refs })
  });
  return handleResponse(res, m);
}

export async function triggerReadwiseSync(full = false) {
  const m = perfMark('POST /sync-readwise');
  const res = await fetch(`${API_BASE}/sync-readwise?full=${full}`, { method: 'POST' });
  return handleResponse(res, m);
}

export async function fetchSyncStatus() {
  const m = perfMark('GET /sync-status');
  const res = await fetch(`${API_BASE}/sync-status`);
  return handleResponse(res, m);
}

export async function fetchHighlightZettelMap() {
  const m = perfMark('GET /highlight-zettel-map');
  const res = await fetch(`${API_BASE}/highlight-zettel-map`);
  return handleResponse(res, m);
}

export async function unlinkZettel(zettelId) {
  const m = perfMark('POST /unlink-zettel');
  const res = await fetch(`${API_BASE}/unlink-zettel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ zettelId })
  });
  return handleResponse(res, m);
}

export async function updateReadwiseHighlight(sourceKey, highlightIndex, { text, note }) {
  const m = perfMark('PATCH /readwise-highlight');
  const res = await fetch(`${API_BASE}/readwise-highlight`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sourceKey, highlightIndex, text, note })
  });
  return handleResponse(res, m);
}

export async function deleteReadwiseHighlight(sourceKey, highlightIndex, highlightId) {
  const m = perfMark('DELETE /readwise-highlight');
  const res = await fetch(`${API_BASE}/readwise-highlight`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sourceKey, highlightIndex, highlightId })
  });
  return handleResponse(res, m);
}

export async function fetchReadwiseTrash() {
  const m = perfMark('GET /readwise-trash');
  const res = await fetch(`${API_BASE}/readwise-trash`);
  return handleResponse(res, m);
}

export async function emptyReadwiseTrash() {
  const m = perfMark('DELETE /readwise-trash');
  const res = await fetch(`${API_BASE}/readwise-trash`, { method: 'DELETE' });
  return handleResponse(res, m);
}

export async function fetchHighlightStatus() {
  const m = perfMark('GET /highlight-status');
  const res = await fetch(`${API_BASE}/highlight-status`);
  return handleResponse(res, m);
}

export async function setHighlightStatus(ref, status) {
  const m = perfMark('PATCH /highlight-status');
  const res = await fetch(`${API_BASE}/highlight-status/${encodeURIComponent(ref)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status })
  });
  return handleResponse(res, m);
}

// --- Eigene Quellen (Custom Sources) ---

export async function createCustomSource(data) {
  const m = perfMark('POST /custom-source');
  const res = await fetch(`${API_BASE}/custom-source`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  return handleResponse(res, m);
}

export async function updateCustomSource(customId, data) {
  const m = perfMark('PATCH /custom-source');
  const res = await fetch(`${API_BASE}/custom-source/${encodeURIComponent(customId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  return handleResponse(res, m);
}

export async function deleteCustomSource(customId) {
  const m = perfMark('DELETE /custom-source');
  const res = await fetch(`${API_BASE}/custom-source/${encodeURIComponent(customId)}`, {
    method: 'DELETE'
  });
  return handleResponse(res, m);
}

export async function addCustomHighlight(customId, data) {
  const m = perfMark('POST /custom-source/highlight');
  const res = await fetch(`${API_BASE}/custom-source/${encodeURIComponent(customId)}/highlight`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  return handleResponse(res, m);
}

export async function updateCustomHighlight(customId, index, data) {
  const m = perfMark('PATCH /custom-source/highlight');
  const res = await fetch(`${API_BASE}/custom-source/${encodeURIComponent(customId)}/highlight/${index}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  return handleResponse(res, m);
}

export async function deleteCustomHighlight(customId, index) {
  const m = perfMark('DELETE /custom-source/highlight');
  const res = await fetch(`${API_BASE}/custom-source/${encodeURIComponent(customId)}/highlight/${index}`, {
    method: 'DELETE'
  });
  return handleResponse(res, m);
}

export async function fetchQuotes() {
  const m = perfMark('GET /quotes');
  const res = await fetch(`${API_BASE}/quotes`);
  return handleResponse(res, m);
}

export async function fetchSettings() {
  const m = perfMark('GET /settings');
  const res = await fetch(`${API_BASE}/settings`);
  return handleResponse(res, m);
}

export async function updateSettings(data) {
  const m = perfMark('PUT /settings');
  const res = await fetch(`${API_BASE}/settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  return handleResponse(res, m);
}

export async function createNewInstance() {
  const m = perfMark('POST /new-instance');
  const res = await fetch(`${API_BASE}/new-instance`, { method: 'POST' });
  return handleResponse(res, m);
}

// SEV-009 Fix: Kurzlebigen Token vom Server holen, dann erst Reset ausführen
export async function fetchResetToken() {
  const m = perfMark('GET /reset-token');
  const res = await fetch(`${API_BASE}/reset-token`);
  return handleResponse(res, m);
}

export async function resetApp(token) {
  const m = perfMark('POST /reset');
  const res = await fetch(`${API_BASE}/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirm: 'RESET_CONFIRMED', token }),
  });
  return handleResponse(res, m);
}

export async function uploadImage(file) {
  const m = perfMark('POST /attachments');
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = reader.result.split(',')[1];
        const res = await fetch(`${API_BASE}/attachments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: file.name, data: base64 })
        });
        const data = await handleResponse(res, m);
        resolve(data.filename);
      } catch (err) {
        perfEnd(m, { error: true });
        reject(err);
      }
    };
    reader.onerror = (err) => { perfEnd(m, { error: true }); reject(err); };
    reader.readAsDataURL(file);
  });
}

// === Debug: Server-Performance-Report abrufen ===
// --- Update-Check: Prüft ob eine neuere Version verfügbar ist (#87) ---
const UPDATE_CHECK_URL = 'https://luhm.app/version.json';

export async function checkForUpdate(currentVersion) {
  try {
    const res = await fetch(UPDATE_CHECK_URL, { cache: 'no-cache' });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.latest || !currentVersion) return null;
    // Einfacher Semver-Vergleich: "0.26.5" vs "0.26.6"
    const current = currentVersion.split('.').map(Number);
    const latest = data.latest.split('.').map(Number);
    for (let i = 0; i < 3; i++) {
      if ((latest[i] || 0) > (current[i] || 0)) return data; // Neuer!
      if ((latest[i] || 0) < (current[i] || 0)) return null; // Älter/gleich
    }
    return null; // Gleiche Version
  } catch (e) {
    // Update-Check darf nie die App blockieren
    return null;
  }
}

export async function fetchDebugPerfReport() {
  const m = perfMark('GET /debug/perf-report');
  const res = await fetch(`${API_BASE}/debug/perf-report`);
  return handleResponse(res, m);
}

// === Kombinierter Debug-Report: Client + Server ===
window.__fullPerfReport = async () => {
  const clientReport = window.__perfReport();
  try {
    const serverReport = await fetchDebugPerfReport();
    const combined = { client: clientReport, server: serverReport };
    console.log('📊 VOLLSTÄNDIGER PERFORMANCE-REPORT (Client + Server):', JSON.stringify(combined, null, 2));
    return combined;
  } catch (e) {
    console.warn('Server-Report nicht verfügbar:', e.message);
    return { client: clientReport, server: null };
  }
};
